import { AnimatePresence, motion } from 'motion/react';
import MenuButton from './MenuButton';
import IconCross from './IconCross';
import { useAppStore } from '../stores/useAppStore';
import CloseMenuButton from './CloseMenuButton';
import { useEffect, useState } from 'react';
import useMenuNavigation from '../hooks/useMenuNavigation';

const MotionCloseMenuButton = motion.create(CloseMenuButton);

function Sidebar({items}) {
  const { sidebarCollapsed, setSidebarCollapsed, fullScreen } = useAppStore();
  const [ collapsed, setCollapsed ] = useState(sidebarCollapsed || fullScreen);
  const navigateTo = useMenuNavigation();

  useEffect(() => {
    setCollapsed(sidebarCollapsed || fullScreen);
  }, [sidebarCollapsed, fullScreen]);

  const handleButtonClick = (key) => {
    navigateTo(key);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const sidebarVariants = {
    expanded: {
      width: '36rem',
      transition: {
        duration: 0.3,
        ease: 'linear'
      }
    },
    collapsed: {
      width: fullScreen ? "0px" : '15rem',
      transition: {
        duration: 0.3,
        ease: 'linear'
      }
    }
  };

  const menuVariants = {
    expanded: {
      x: 0,
      y: '-50%',
      transition: {
        duration: 0.3,
        ease: 'linear'
      }
    },
    collapsed: {
      x: '-100%',
      y: '-50%',
      transition: {
        duration: 0.3,
        ease: 'linear'
      }
    }
  };

  // Definir variantes para el botón de colapsar
  const closeButtonVariants = {
    hidden: { opacity: 0, x: "-100%" },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { 
        duration: 0.3, 
        ease: 'linear' 
      },
    },
    exit: { 
      opacity: 0, 
      x: "-100%",
      transition: { 
        duration: 0.3, 
        ease: 'linear' 
      },
    },
  };

  // Definir variantes para el botón "MENU +"
  const openButtonVariants = {
    hidden: { 
      opacity: 0,
      x: '-100%',
      y: '-50%',
      rotate: -90
    },
    visible: { 
      x: '-4.25rem',
      y: '-50%',
      rotate: -90,
      opacity: 1,
      transition: { 
        duration: 0.3, 
        ease: 'easeInOut', 
        delay: 0.5 // Retardo de 0.3 segundos
      },
    },
    exit: { 
      x: '-100%',
      y: '-50%',
      rotate: -90,
      opacity: 0, 
      transition: { 
        duration: 0.3, 
        ease: 'easeInOut' 
      },
    },
  };

  return (
    <motion.div
      variants={sidebarVariants}
      animate={collapsed ? 'collapsed' : 'expanded'}
      className={`
        flex flex-col
        bg-[#081f3f] text-white
        h-full
        overflow-hidden
      `}
    >

      {/* Menú de navegación */}
      <motion.nav className="flex-1 absolute left-0 top-1/2"
      variants={menuVariants}
      animate={collapsed ? 'collapsed' : 'expanded'}>
        <ul className="flex flex-col gap-9 w-[35.4375rem]">
        {items.map((item, index) => (
          <motion.li 
          key={item.key}
          initial={{ opacity: 0, x: "-5%" }}
          animate={{
            opacity: collapsed ? 0 : 1,
            x: collapsed ? "-5%" : 0,
            transition: {
              duration: 0.3,
              ease: 'easeInOut',
              delay: collapsed ? (0.1 * (items.length - index)) : (0.1 * index)
            }
          }}
        >
            <MenuButton
                item={item}
                onClick={() => handleButtonClick(item.key)}
            >
            </MenuButton>
          </motion.li>
        ))}
        </ul>
      </motion.nav>

      {/* Botón en la parte inferior para colapsar */}
      <AnimatePresence>
        {!collapsed && (
          <MotionCloseMenuButton
            key="collapse-button" // Clave única
            variants={closeButtonVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={{ duration: 0.3, ease: 'linear' }}
            onClick={toggleSidebar}
            className="
              click-effect
              absolute
              left-[6.125rem]
              bottom-[5.6875rem]
              w-[6.875rem]
              h-[6.875rem]
              focus:outline-none
            "
           />
        )}
        {collapsed && !fullScreen && (
          <motion.button
            key="open-button" // Clave única
            variants={openButtonVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={toggleSidebar}
            className="
              click-effect
              absolute
              top-1/2
              flex
              gap-[8.75rem]
              items-center
              justify-center
              rounded-full
              focus:outline-none
              bg-gradient-animated
              px-16
              py-[2.5rem]

              before:content-['']
              before:w-[calc(100%-0.5rem)]
              before:h-[calc(100%-0.5rem)]
              before:bg-navy
              before:flex
              before:items-center
              before:justify-center
              before:rounded-full
              before:absolute
              before:left-[0.25rem]
              before:top-[0.25rem]
              before:-z-10
            "
          >
                <div className='text-[3rem]
              font-light
              tracking-widest'>Menu</div>
                <IconCross className='h-[3.125rem] w-[3.125rem]' />
          </motion.button>
        )}
      </AnimatePresence>

    </motion.div>
  );
}

export default Sidebar;